import { useEffect, useRef, useState } from "react";
import { useCountdown } from "@/hooks/useCountdown";
import { CheckCircle2 } from "lucide-react";
import { boardKeyFromName } from "@/components/BoardTracker";
import { getGlowColor } from "@/lib/glowUtils";

const LONG_PRESS_MS = 1000;

function pad(n) {
  return n.toString().padStart(2, "0");
}

export default function CompactTimerCard({ card, onClick, onClaim, onProxyClaim, onReadyTransition }) {
  const [isHovered, setIsHovered] = useState(false);
  const { days, hours, minutes, seconds, isReady } = useCountdown(card.target_at);
  const totalMinutes = days * 1440 + hours * 60 + minutes;
  const boardKey = boardKeyFromName(card.name);

  // Long-press tracking for touch devices
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  const getCardColor = (cardName) => {
    if (cardName.includes('Red')) return 'text-red-300';
    if (cardName.includes('Blue')) return 'text-blue-300';
    if (cardName.includes('Board')) return 'text-orange-300';
    if (cardName.includes('Wasteland')) return 'text-blue-300';
    if (cardName.includes('Crypt')) return 'text-green-300';
    if (cardName.includes('Last Resort')) return 'text-yellow-300';
    return 'text-orange-300';
  };

  const cardColor = getCardColor(card.name);

  useEffect(() => {
    if (isReady && !card.notified) {
      onReadyTransition?.(card);
    }
  }, [isReady, card, onReadyTransition]);

  const stateClass = isReady
    ? "border-emerald-500/60 animate-pulse-ready"
    : "border-orange-500/25 hover:border-orange-400/60";

  const handleRoot = (e) => {
    if (e.target.closest("[data-claim-btn]")) return;
    onClick?.(card);
  };

  return (
    <button
      type="button"
      onClick={handleRoot}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`relative border bg-[#121822] p-3 text-left w-full transition-colors duration-300 corner-tl corner-tr corner-bl corner-br ${isHovered ? 'bg-slate-800/20' : ''} ${stateClass}`}
      data-testid={`compact-card-${card.id}`}
    >
      <div className="text-[11px] tracking-[0.3em] uppercase text-slate-300 truncate">
        {card.source || "—"}
      </div>
      <div
        className={`font-bold mt-0.5 mb-2 text-slate-200 truncate`}
        style={{ 
          fontSize: 'clamp(0.7rem, 3vw, 1rem)',
          textShadow: `0 0 5px ${getGlowColor(cardColor)}, 0 0 10px ${getGlowColor(cardColor)}, 0 0 20px ${getGlowColor(cardColor)}, 0 0 30px ${getGlowColor(cardColor)}`
        }}
        title={card.name}
      >
        {card.name}
      </div>

      <div className="min-h-[44px] flex items-center justify-center">
        {isReady ? (
          <button
            type="button"
            data-claim-btn
            data-testid={`compact-claim-${card.id}`}
            onClick={(e) => {
              e.stopPropagation();
              // Ignore click if it was preceded by a long-press on touch
              if (longPressFired.current) {
                longPressFired.current = false;
                return;
              }
              onClaim?.(card, boardKey);
            }}
            onMouseDown={(e) => {
              // Middle mouse button (button === 1) → proxy claim
              if (e.button === 1) {
                e.preventDefault();
                e.stopPropagation();
                onProxyClaim?.(card, boardKey);
              }
            }}
            onTouchStart={(e) => {
              longPressFired.current = false;
              longPressTimer.current = setTimeout(() => {
                longPressFired.current = true;
                onProxyClaim?.(card, boardKey);
              }, LONG_PRESS_MS);
            }}
            onTouchEnd={(e) => {
              clearTimeout(longPressTimer.current);
            }}
            onTouchMove={() => {
              // Cancel long press if the user scrolls
              clearTimeout(longPressTimer.current);
            }}
            title="Click to claim · Middle-click or long-press to proxy claim"
            className="w-full h-full min-h-[44px] flex items-center justify-center gap-1.5 border border-emerald-400/60 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500 hover:text-black transition-colors text-[11px] tracking-[0.3em] uppercase font-bold"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Claim
          </button>
        ) : (
          <div className="flex items-center justify-center gap-1.5 tabular-nums">
            <span className={`font-dseg7 text-xl leading-none ${cardColor}`} style={{ textShadow: `0 0 10px currentColor, 0 0 20px currentColor` }}>{totalMinutes.toString().padStart(2, "0")}</span>
            <span className="font-dseg7 text-orange-400/40 text-lg leading-none">:</span>
            <span className={`font-dseg7 text-xl leading-none ${cardColor}`} style={{ textShadow: `0 0 10px currentColor, 0 0 20px currentColor` }}>{pad(seconds)}</span>
          </div>
        )}
      </div>
    </button>
  );
}
